import type React from "react"
import { useEffect } from "react"
import { Link, useNavigate, useSearchParams } from "react-router-dom"
import styled from "styled-components"
import backgroundImage from "~/assets/arbeidsark-bg-illustration.svg"
import { Button, Form, FormField, Input, Label } from "~/components"
import { Margin } from "~/ur-react-components"
import { useForm, useTranslate } from "~/ur-react-hooks"
import { useKeyDown } from "~/utils"
import { trpc } from "~/utils/trpc"
import { validatePassword } from "~/utils/validation"
import { setAuthToken } from "./util"

const Wrapper = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  height: 100%;

  background-image: url(${backgroundImage});

  ${(props) =>
    props.theme.media.mobile &&
    `
      width: 100%;
  `}
`

const Title = styled.h3`
  font-family: 'Work Sans';
  font-style: normal;
  font-weight: 600;
  font-size: 18px;
  line-height: 145%;
`

const Card = styled.div`
  display: flex;
  flex-direction: column;
  width: 394px;
  box-sizing: border-box;

  background: white;
  border-radius: 8px;
  padding: 24px;
  box-shadow: 0px 8px 32px 0px rgba(27, 12, 55, 0.12);
`

const BottomSection = styled.div`
  text-align: center;
  font-size: 14px;
  margin-top: 2rem;
  margin-bottom: 1rem;
`

const RegisterUserLink = styled(Link)`
  display: block;
  color: ${(props) => props.theme.colors.primary};
  font-weight: bold;
`

type ConfirmResetPasswordProps = {}

export const ConfirmResetPassword: React.FC<ConfirmResetPasswordProps> = () => {
  const translations = useTranslate({
    title: "confirm-reset-password.title",
    backToLogin: "confirm-reset-password.back-to-login",
    submit: "common.submit",
    password: "common.password",
    passwordAgain: "login.repeat-password",
    notAlreadyRegistered: "login.not-already-registered",
    createUser: "login.create-user",
    forgottenPassword: "login.forgotten-password",
    error: {
      createUser: "login.create-user-error",
      emailTaken: "errors.from-code.email-already-taken",
      wrongUsernameOrPassword: "error.wrong-username-or-password",
      required: ["error.field-is-required", { field: "" }],
      invalidEmail: "errors.invalid-email-address",
      badPasswords: "errors.change-password-weak",
      nonMatchingPasswords: "errors.passwords-do-not-match",
    },
  })

  const navigate = useNavigate()
  const [search] = useSearchParams()

  const token = search.get("token")

  useEffect(() => {
    if (!token) {
      navigate("/login")
    }
  }, [navigate, token])

  const {
    formValues: form,
    formChangeHandler: handler,
    submitHandler: submit,
    formValid,
    formEdited,
  } = useForm({
    values: {
      password: "",
      passwordAgain: "",
    },
    validators: {
      password: validatePassword(
        translations.error.badPasswords,
        "passwordAgain",
        translations.error.nonMatchingPasswords,
      ),
      passwordAgain: validatePassword(
        translations.error.nonMatchingPasswords,
        "password",
        translations.error.nonMatchingPasswords,
      ),
    },
  })

  const handleEnter = useKeyDown("Enter", submit(handleSubmit))

  const { mutate: confirmResetPassword, isPending: isLoading } =
    trpc.authentication.resetPasswordStepTwoRequest.useMutation({
      onSuccess(data) {
        setAuthToken(data.token)
        navigate("/")
      },
    })

  async function handleSubmit() {
    if (!formValid) {
      return
    }

    confirmResetPassword({ token: token ?? "", newPassword: form.password })
  }

  return (
    <Wrapper>
      <Card>
        <Title>{translations.title}</Title>

        <Form preventDefault onSubmit={submit(handleSubmit)}>
          <FormField>
            <Label>{translations.password}</Label>
            <Input
              value={form.password}
              width="100%"
              type="password"
              onKeyDown={handleEnter}
              onChange={handler("password")}
            />
          </FormField>
          <FormField>
            <Label>{translations.passwordAgain}</Label>
            <Input
              value={form.passwordAgain}
              width="100%"
              type="password"
              onKeyDown={handleEnter}
              onChange={handler("passwordAgain")}
            />
          </FormField>
          <Margin top="1.5rem">
            <Button
              width="100%"
              disabled={!formValid || !formEdited || isLoading}
            >
              {translations.submit}
            </Button>
          </Margin>
        </Form>
        <BottomSection>
          <RegisterUserLink to="/login">
            {translations.backToLogin}
          </RegisterUserLink>
        </BottomSection>
      </Card>
    </Wrapper>
  )
}

export default ConfirmResetPassword
